import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import { stcSortingBoxDefService, StcSortingBoxDefDto, UpdateStcSortingBoxDefRequest } from '../../../../../services/stcSortingBoxDefService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { SortingBoxDefSidePanel } from './SortingBoxDefSidePanel';
import { SortingBoxDefCreateModal } from './SortingBoxDefCreateModal';

interface SortingBoxDefDataTableProps {
  refreshTrigger?: number;
}

export const SortingBoxDefDataTable: React.FC<SortingBoxDefDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';

  // Core data state
  const [data, setData] = useState<StcSortingBoxDefDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Modal state
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Inline editing
  const inlineEditConfig = useInlineEdit<StcSortingBoxDefDto>({
    getRowId: (row) => row.id,
    onSave: async (_originalRow, editedData) => {
      const request: UpdateStcSortingBoxDefRequest = {
        description1: (editedData.description1 as string) || null,
        width: editedData.width as number | null | undefined,
        depth: editedData.depth as number | null | undefined,
        height: editedData.height as number | null | undefined,
        totalVolume: editedData.totalVolume as number | null | undefined
      };
      await stcSortingBoxDefService.update(
        _originalRow.companyNo,
        _originalRow.stcBoxTypeID,
        request
      );
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || t('manager.modules.adminResources.sortingBoxTable.errorSave', {}, 'Feil ved lagring'))
  });

  // Load data
  useEffect(() => {
    loadData();
  }, [companyNo]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadData();
    }
  }, [refreshTrigger]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      const sortingBoxData = await stcSortingBoxDefService.getCurrent().catch(() =>
        stcSortingBoxDefService.getAll().catch(() => [])
      );
      setData(sortingBoxData);
    } catch (err: any) {
      console.error('Error loading sorting box def data:', err);
      setError(t('manager.modules.adminResources.sortingBoxTable.errorLoad', {}, 'Kunne ikke laste sorteringsboks definisjoner. Prøv igjen.'));
    } finally {
      setIsLoading(false);
    }
  }, [companyNo, t]);

  // Column factory
  const columnFactory = createColumnFactory<StcSortingBoxDefDto>();

  // Column definitions
  const columns: ColumnDefinition<StcSortingBoxDefDto>[] = useMemo(() => [
    columnFactory.createTextColumn('stcBoxTypeID', 'stcBoxTypeID', t('manager.modules.adminResources.sortingBox.boxTypeIdLabel', {}, 'Boks type ID'), {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
      minWidth: 200,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('width', 'width', t('manager.modules.adminResources.sortingBox.widthLabel', {}, 'Bredde'), {
      minWidth: 100,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),

    columnFactory.createNumberColumn('depth', 'depth', t('manager.modules.adminResources.sortingBox.depthLabel', {}, 'Dybde'), {
      minWidth: 100,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),

    columnFactory.createNumberColumn('height', 'height', t('manager.modules.adminResources.sortingBox.heightLabel', {}, 'Høyde'), {
      minWidth: 100,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),

    columnFactory.createNumberColumn('totalVolume', 'totalVolume', t('manager.modules.adminResources.sortingBox.totalVolumeLabel', {}, 'Totalvolum'), {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    })
  ], [t]);

  // Handle bulk edit
  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert(t('manager.modules.adminResources.sortingBoxTable.alertSelectAtLeastOne', {}, 'Velg minst én sorteringsboks for å redigere'));
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length, t]);

  // Table actions
  const tableActions: TableAction<StcSortingBoxDefDto>[] = useMemo(() => [
    {
      id: 'create',
      label: t('manager.modules.adminResources.sortingBoxTable.newSortingBox', {}, 'Ny sorteringsboks'),
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: t('manager.modules.adminResources.common.maintenanceAction', { '0': selectedIds.length }, `Vedlikehold (${selectedIds.length})`),
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleBulkEdit, t]);

  // Build selected rows map for SortingBoxDefSidePanel compatibility
  const selectedRowsMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    selectedIds.forEach(id => { map[String(id)] = true; });
    return map;
  }, [selectedIds]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={data}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'sorting-box-defs-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.id}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Bulk Edit Side Panel */}
      {showBulkEditModal && selectedIds.length > 0 && (
        <SortingBoxDefSidePanel
          selectedRows={selectedRowsMap}
          data={data}
          onClose={() => setShowBulkEditModal(false)}
          onSave={() => {
            loadData();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <SortingBoxDefCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadData();
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};
